
import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  /** Ratio of element visibility required to trigger the animation (0‑1). */
  threshold?: number;
  /** Margin around the root element for the observer. */
  rootMargin?: string;
  /** If true, the animation triggers only once. */
  once?: boolean;
}

/**
 * Hook that toggles `isVisible` when the attached element enters or leaves the viewport.
 *
 * @param options Configuration for the IntersectionObserver.
 * @returns An object containing a `ref` to attach to the target element and a boolean `isVisible`.
 */
export function useScrollAnimation(
  options: UseScrollAnimationOptions = {}
) {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -60px 0px',
    once = true,
  } = options;

  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Guard against environments where IntersectionObserver is not available
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: consider the element visible immediately
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(entry.target);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    // Cleanup on unmount or when dependencies change
    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}
